// Shared state for every project card on the page, so twenty cards cost two requests
// and not forty:
//  - useProviderStatus("acc" | "procore"): the signed-in user's connection, asked once
//    and kept for a minute (the logos are in colour when connected);
//  - useIntegrationCard(projectId): the project's Procore link and delivery folders,
//    asked for every mounted card in one GET /integrations?projects=... .
import { useEffect, useState } from "react";
import { getAccStatus } from "components/Acc/accApi";
import { getProcoreStatus, getIntegrationCards } from "./integrationsApi";

// ---- connection status ------------------------------------------------------------------

const STATUS_TTL = 60000;
const statusCache = { acc: null, procore: null }; // { at, value, promise }
const statusWatchers = { acc: new Set(), procore: new Set() };
const fetchers = { acc: getAccStatus, procore: getProcoreStatus };

function loadStatus(provider, force = false) {
  const c = statusCache[provider];
  if (!force && c && (c.promise || Date.now() - c.at < STATUS_TTL)) return c.promise || Promise.resolve(c.value);
  const promise = fetchers[provider]()
    .catch((e) => ({ configured: true, connected: false, error: e.message }))
    .then((value) => {
      statusCache[provider] = { at: Date.now(), value, promise: null };
      for (const set of statusWatchers[provider]) set(value);
      return value;
    });
  statusCache[provider] = { ...(c || {}), promise };
  return promise;
}

/** Forget a provider's status (after connecting or disconnecting) and ask again. */
export function refreshProviderStatus(provider) {
  return loadStatus(provider, true);
}

export function useProviderStatus(provider) {
  const c = statusCache[provider];
  const [status, setStatus] = useState(c && c.value ? c.value : null);
  useEffect(() => {
    const w = statusWatchers[provider];
    w.add(setStatus);
    loadStatus(provider).then(setStatus);
    return () => w.delete(setStatus);
  }, [provider]);
  return status; // null until the first answer
}

// ---- per-project cards ------------------------------------------------------------------

const POLL_MS = 60000;
const cardWatchers = new Map(); // projectId -> Set(setState)
const cards = new Map(); // projectId -> { acc, procore } | null
let timer = null;
let inFlight = false;

function publish() {
  for (const [id, sets] of cardWatchers) for (const set of sets) set(cards.has(id) ? cards.get(id) : null);
}

function schedule(ms) {
  clearTimeout(timer);
  timer = cardWatchers.size ? setTimeout(poll, ms) : null;
}

async function poll() {
  if (inFlight || !cardWatchers.size) return;
  inFlight = true;
  const ids = [...cardWatchers.keys()];
  try {
    const byId = (await getIntegrationCards(ids)) || {};
    for (const id of ids) cards.set(id, byId[id] || null);
    publish();
  } catch (e) {
    // keep the last state; the next poll tries again
  } finally {
    inFlight = false;
    schedule([...cardWatchers.keys()].some((id) => !cards.has(id)) ? 100 : POLL_MS);
  }
}

/** Ask again now (after saving a project's settings). */
export function refreshIntegrationCards() {
  clearTimeout(timer);
  setTimeout(poll, 50);
}

export function useIntegrationCard(projectId) {
  const id = String(projectId || "");
  const [state, setState] = useState(cards.has(id) ? cards.get(id) : undefined);
  useEffect(() => {
    if (!id) return undefined;
    if (!cardWatchers.has(id)) cardWatchers.set(id, new Set());
    cardWatchers.get(id).add(setState);
    if (!inFlight) schedule(100);
    return () => {
      const s = cardWatchers.get(id);
      if (s) {
        s.delete(setState);
        if (!s.size) cardWatchers.delete(id);
      }
      if (!cardWatchers.size) schedule(0);
    };
  }, [id]);
  return state; // undefined = not asked yet, null = nothing set, else { acc, procore }
}
